import { useEffect, useState } from "react";
import { Button, Space, message } from "antd";
import { Link, useNavigate } from "react-router";
import request from "@/api";
import { refundService } from "@/api/services/refundService";
import { receivePaymentService } from "@/api/services/receivePaymentService";
import { fileService } from "@/api/services/fileService";
import { REFUND_STATE_LABELS } from "@/views/pages/tradeManage/refundState";
import { useUserStore } from "@/stores/react/authStore";
import { confirmDialog } from "@/utils/confirmDialog";
import { showRequestError } from "@/utils/requestErrorMessage";
import { RECEIVE_STATE, REFUND_STATE, TRADE_ORDER_STATE } from "@zhihaoscm/meta";
import {
    ProductDetailSubTabs,
    ProductDetailTabSection,
    ProductDetailTabTable,
    ProductDetailTabMetrics,
    emptyProductDetailColumns,
    withProductDetailDateRange,
    type ProductDetailFilterValues,
    type ProductDetailTabProps
} from "@/views/pages/productManagement/ProductDetailTabPrimitives";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

export type indexProps = ProductDetailTabProps;
/** 收款明细的支付状态需覆盖 RECEIVE_STATE 全部状态，避免有效状态落到空值占位符。 */
const PAYMENT_STATE_LABELS: Record<number, string> = {
    1: "待支付",
    2: "已支付 (线上)",
    3: "未支付",
    4: "待确认支付",
    5: "已支付 (线下)"
};
const PAYMENT_TYPE_LABELS: Record<number, string> = { 1: "订单定金", 3: "发航货款" };
const numberText = (value: unknown) =>
    value === null || value === undefined
        ? "--"
        : Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dateTimeText = (value: unknown) =>
    String(value || "--")
        .replace("T", " ")
        .slice(0, 16);

/** components / feeTab / index：保留数据展示、编辑提交和父级事件回调。 */
export default function MigrationComponent(props: indexProps) {
    const navigate = useNavigate();
    const [active, setActive] = useState("收款");
    const productId = String(props.data?.productConsignment?.id || props.data?.id || "");
    const [amount, setAmount] = useState({ receivePaymentAmount: 0, paidAmount: 0, pendingPaymentAmount: 0 });
    const [refundAmount, setRefundAmount] = useState({ refundTotalAmount: 0, refundedAmount: 0 });
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState<ProductDetailFilterValues>({});
    const [pageable, setPageable] = useState({ page: 1, size: 10 });
    const [reloadToken, setReloadToken] = useState(0);
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const canProductRead = permissions.includes("ROLE_PRODUCT_R");
    const canProductManage = permissions.includes("ROLE_PRODUCT_MANAGE");
    const canRefundDeal = permissions.includes("ROLE_REFUND_DEAL");
    const canRefundManage = permissions.includes("ROLE_REFUND_MANAGE");

    /** 文件类操作沿用旧版文件服务，在新窗口打开真实凭证。 */
    const previewFile = async (fileId: string | number | undefined, emptyMessage: string) => {
        if (!fileId) {
            message.warning(emptyMessage);
            return;
        }
        try {
            const file = await fileService.getFileById(Number(fileId));
            if (file.path) window.open(file.path, "_blank");
        } catch (error) {
            showRequestError(error, "凭证预览失败");
        }
    };

    /** 收款删除保留 Vue 的确认提示，成功后重新请求金额和列表。 */
    const deletePayment = async (id: string) => {
        if (!(await confirmDialog("是否删除，删除后支付记录不可恢复", "是否删除"))) return;
        try {
            await receivePaymentService.delete(id);
            notifyOperationSuccess("已删除");
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "收款记录删除失败");
        }
    };

    /** 退款取消使用旧版 DELETE 接口，避免 service 装饰器叠加两次确认框。 */
    const cancelRefund = async (id: string) => {
        if (!(await confirmDialog("是否取消"))) return;
        try {
            await request.service.delete(`/admin/refund/${id}`);
            notifyOperationSuccess("取消成功");
            setReloadToken(value => value + 1);
        } catch (error) {
            showRequestError(error, "退款取消失败");
        }
    };
    useEffect(() => {
        let disposed = false;
        if (!productId) return;
        if (active === "退款") {
            // 退款子页签与旧 Vue refundList 使用同一分页和金额接口，切换页签时再加载。
            void Promise.allSettled([
                refundService.getAmount({ productId, productType: 1 }),
                refundService.paging({
                    page: pageable.page,
                    size: pageable.size,
                    productId,
                    productType: 1,
                    ...withProductDetailDateRange(filters, "createDate")
                } as never)
            ])
                .then(([summaryResult, pageResult]) => {
                    if (disposed) return;
                    // 金额汇总失败时仍渲染成功返回的退款列表，避免整个子页签被清空。
                    if (summaryResult.status === "fulfilled") {
                        setRefundAmount(summaryResult.value || { refundTotalAmount: 0, refundedAmount: 0 });
                    }
                    if (pageResult.status !== "fulfilled") {
                        setRows([]);
                        setTotal(0);
                        return;
                    }
                    const pageResponse = pageResult.value;
                    setTotal(Number(pageResponse?.total || pageResponse?.list?.length || 0));
                    setRows(
                        (pageResponse?.list || []).map((item: any, index: number) => {
                            const state = Number(item.state);
                            return {
                                id: item.id || `refund-${index}`,
                                退款编号: item.id ? (
                                    <Link to={`/tradeManage/refund/detail?id=${item.id}`}>{item.id}</Link>
                                ) : (
                                    "--"
                                ),
                                关联订单: item.orderId || "--",
                                采购用户: item.customerEnterprise
                                    ? `${item.customerEnterprise.name || item.customerEnterprise.realName || ""}(${item.customerEnterprise.mobile || ""})`
                                    : "--",
                                退款类型: item.refundType || "--",
                                退款说明: item.remark || "--",
                                "退款金额(元)": numberText(item.amount),
                                退款时间: dateTimeText(item.refundTime),
                                创建时间: dateTimeText(item.createdTime || item.createTime),
                                状态: REFUND_STATE_LABELS[Number(item.state)] || item.stateName || "--",
                                操作: (
                                    <Space size={0}>
                                        {state === Number(REFUND_STATE.AWAITING_REVIEW.code) && canRefundManage ? (
                                            <Button
                                                aria-label={`审核退款-${item.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() =>
                                                    navigate(
                                                        `/tradeManage/refund/audit?id=${item.id}&time=${Date.now()}`
                                                    )
                                                }
                                            >
                                                审核
                                            </Button>
                                        ) : null}
                                        {state === Number(REFUND_STATE.REJECTED.code) && canRefundDeal ? (
                                            <Button
                                                aria-label={`修改退款-${item.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() =>
                                                    navigate(
                                                        `/tradeManage/refund/edit?id=${item.id}&time=${Date.now()}`
                                                    )
                                                }
                                            >
                                                修改
                                            </Button>
                                        ) : null}
                                        {state === Number(REFUND_STATE.AWAITING_REVIEW.code) && canRefundManage ? (
                                            <Button
                                                aria-label={`取消退款-${item.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() => void cancelRefund(String(item.id))}
                                            >
                                                取消
                                            </Button>
                                        ) : null}
                                        {state === Number(REFUND_STATE.REFUNDED.code) ? (
                                            <Button
                                                aria-label={`预览退款凭证-${item.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() => void previewFile(item.fileId, "未上传退款凭证")}
                                            >
                                                预览
                                            </Button>
                                        ) : null}
                                    </Space>
                                )
                            };
                        })
                    );
                })
                .catch(() => {
                    if (!disposed) setRows([]);
                });
            return () => {
                disposed = true;
            };
        }
        // 收款子页签沿用旧 Vue 的金额汇总及分页接口。
        void Promise.allSettled([
            request.service.get(`/admin/receive-payment/amount`, { params: { productId, productType: 1 } }),
            request.service.get(`/admin/receive-payment/paging`, {
                params: {
                    page: pageable.page,
                    size: pageable.size,
                    productId,
                    productType: 1,
                    ...withProductDetailDateRange(filters, "createDate")
                }
            })
        ])
            .then(([amountResult, pageResult]) => {
                if (disposed) return;
                // 收款汇总与列表分别容错，任一失败不丢弃另一接口已经返回的业务数据。
                if (amountResult.status === "fulfilled") {
                    setAmount(
                        amountResult.value.data || { receivePaymentAmount: 0, paidAmount: 0, pendingPaymentAmount: 0 }
                    );
                }
                if (pageResult.status !== "fulfilled") {
                    setRows([]);
                    setTotal(0);
                    return;
                }
                const pageResponse = pageResult.value;
                setTotal(Number(pageResponse.data?.total || pageResponse.data?.list?.length || 0));
                setRows(
                    (pageResponse.data?.list || []).map((item: any, index: number) => {
                        const payment = item.receivePayment || {};
                        const state = Number(payment.state);
                        const canPreview =
                            canProductRead &&
                            ((payment.customFileId?.length &&
                                [
                                    Number(RECEIVE_STATE.PENDING_CONFIRM.code),
                                    Number(RECEIVE_STATE.UNPAID.code)
                                ].includes(state)) ||
                                (state === Number(RECEIVE_STATE.OFFLINE_PAID.code) && payment.paymentFileId?.length));
                        const orderState = Number(item.orderState ?? item.order?.state);
                        const canDelete =
                            canProductManage &&
                            (item.orderDel || orderState === Number(TRADE_ORDER_STATE.CANCELLED.code)) &&
                            [Number(RECEIVE_STATE.UNPAID.code), Number(RECEIVE_STATE.OFFLINE_PAID.code)].includes(
                                state
                            );
                        return {
                            id: item.receivePayment?.id || item.id || `payment-${index}`,
                            收款编号: payment.id ? (
                                <Link to={`/tradeManage/collection/detail?id=${payment.id}`}>
                                    {payment.paymentId || payment.id}
                                </Link>
                            ) : (
                                "--"
                            ),
                            关联订单: payment.orderId ? (
                                <Link to={`/tradeManage/tradeOrder/detail?id=${payment.orderId}`}>
                                    {payment.orderId}
                                </Link>
                            ) : (
                                "--"
                            ),
                            采购用户: item.receivePayment?.customerEnterprise
                                ? `${item.receivePayment.customerEnterprise.name || item.receivePayment.customerEnterprise.realName || ""}(${item.receivePayment.customerEnterprise.mobile || ""})`
                                : "--",
                            费用类型: PAYMENT_TYPE_LABELS[Number(item.receivePayment?.paymentType)] || "--",
                            费用说明: item.receivePayment?.remark || "--",
                            "支付金额(元)": numberText(item.receivePayment?.amount),
                            支付时间: dateTimeText(item.receivePayment?.payTime),
                            付款说明: item.receivePayment?.paymentInstruction || "--",
                            确认支付凭证时间: dateTimeText(
                                payment.confirmOperateInfo?.[payment.confirmOperateInfo.length - 1]?.confirmTime ||
                                    payment.confirmPayTime
                            ),
                            创建时间: dateTimeText(item.receivePayment?.createdTime),
                            支付状态:
                                PAYMENT_STATE_LABELS[Number(item.receivePayment?.state)] ||
                                item.receivePayment?.stateName ||
                                "--",
                            状态:
                                PAYMENT_STATE_LABELS[Number(item.receivePayment?.state)] ||
                                item.receivePayment?.stateName ||
                                "--",
                            操作:
                                canPreview || canDelete ? (
                                    <Space size={0}>
                                        {canPreview ? (
                                            <Button
                                                aria-label={`查看收款凭证-${payment.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() => void previewFile(payment.paymentFileId, "未上传凭证")}
                                            >
                                                查看凭证
                                            </Button>
                                        ) : null}
                                        {canDelete ? (
                                            <Button
                                                aria-label={`删除收款-${payment.id}`}
                                                size="small"
                                                type="link"
                                                onClick={() => void deletePayment(String(payment.id))}
                                            >
                                                删除
                                            </Button>
                                        ) : null}
                                    </Space>
                                ) : (
                                    ""
                                )
                        };
                    })
                );
            })
            .catch(() => {
                if (!disposed) setRows([]);
            });
        return () => {
            disposed = true;
        };
    }, [
        productId,
        active,
        filters,
        pageable,
        reloadToken,
        canProductRead,
        canProductManage,
        canRefundDeal,
        canRefundManage,
        navigate
    ]);
    return (
        <ProductDetailTabSection title="费用明细" className="fee-tab" showTitle={false}>
            <ProductDetailSubTabs
                active={active}
                onChange={value => {
                    setFilters({});
                    setPageable(current => ({ ...current, page: 1 }));
                    setActive(value);
                }}
            />
            {active === "收款" && (
                <ProductDetailTabMetrics
                    items={[
                        {
                            label: "收款总金额（元）",
                            value: amount.receivePaymentAmount.toLocaleString(undefined, { minimumFractionDigits: 2 }),
                            type: "money"
                        },
                        {
                            label: "已支付总金额（元）",
                            value: amount.paidAmount.toLocaleString(undefined, { minimumFractionDigits: 2 }),
                            type: "money"
                        },
                        {
                            label: "待支付总金额（元）",
                            value: amount.pendingPaymentAmount.toLocaleString(undefined, { minimumFractionDigits: 2 }),
                            type: "money"
                        }
                    ]}
                />
            )}
            {active === "退款" && (
                <ProductDetailTabMetrics
                    items={[
                        {
                            label: "退款总金额（元）",
                            value: refundAmount.refundTotalAmount.toLocaleString(undefined, {
                                minimumFractionDigits: 2
                            }),
                            type: "money"
                        },
                        {
                            label: "已退款总金额（元）",
                            value: refundAmount.refundedAmount.toLocaleString(undefined, { minimumFractionDigits: 2 }),
                            type: "money"
                        }
                    ]}
                />
            )}
            <ProductDetailTabTable
                ariaLabel={`${active}费用明细`}
                columns={emptyProductDetailColumns(
                    active === "收款"
                        ? [
                              "收款编号",
                              "关联订单",
                              "采购用户",
                              "费用类型",
                              "费用说明",
                              "支付金额(元)",
                              "支付时间",
                              "付款说明",
                              "确认支付凭证时间",
                              "创建时间",
                              "支付状态",
                              "操作"
                          ]
                        : [
                              "退款编号",
                              "关联订单",
                              "采购用户",
                              "退款类型",
                              "退款说明",
                              "退款金额(元)",
                              "退款时间",
                              "创建时间",
                              "状态",
                              "操作"
                          ]
                )}
                data={rows}
                current={pageable.page}
                pageSize={pageable.size}
                total={total}
                onSearch={values => {
                    // 收款与退款筛选共用分页，搜索时统一复位到第一页。
                    setPageable(current => ({ ...current, page: 1 }));
                    setFilters(values);
                }}
                onPageChange={(page, size) => setPageable({ page, size })}
                onCreate={
                    active === "退款" && canRefundDeal
                        ? () => navigate(`/tradeManage/refund/add?time=${Date.now()}`)
                        : undefined
                }
            />
            {props.children}
        </ProductDetailTabSection>
    );
}
